/* global axios */
import ApiClient from './ApiClient';
import store from '../store';
import webhookService from '../services/kanban/webhookService';

const encodeParam = value => encodeURIComponent(String(value));

// O board 2.0 manda params ANINHADOS (`stages[<coluna>][]=<slug>`,
// `cursor[<coluna>]=<ts,id>`). O axios 1.13.2 (sem paramsSerializer configurado
// neste projeto — ver dashboard/helper/APIHelper.js) serializa array aninhado com
// ÍNDICE: `stages[perdido][0]=perdido`. O Rack transforma chave numérica em HASH
// ({"0"=>"perdido"}), não em Array — o controller receberia um Hash em
// requested_columns e a fatia da coluna sairia errada, SEM erro. Este serializer
// emite a forma que o Rack lê como Array: `stages[perdido][]=perdido`.
// Aplicado só quando há params aninhados (ver getItems), para não alterar um byte
// da serialização dos 15+ call sites do v1 que compartilham este endpoint.
export function serializeKanbanParams(params) {
  const parts = [];
  const push = (key, value) => {
    if (value === undefined || value === null) return;
    parts.push(`${encodeParam(key)}=${encodeParam(value)}`);
  };

  Object.entries(params || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(entry => push(`${key}[]`, entry));
      return;
    }
    if (value && typeof value === 'object') {
      Object.entries(value).forEach(([subKey, subValue]) => {
        if (Array.isArray(subValue)) {
          subValue.forEach(entry => push(`${key}[${subKey}][]`, entry));
          return;
        }
        push(`${key}[${subKey}]`, subValue);
      });
      return;
    }
    push(key, value);
  });

  return parts.join('&');
}

class KanbanAPI extends ApiClient {
  constructor() {
    super('kanban_items', { accountScoped: true });
  }

  get accountId() {
    return store.state.auth.currentUser.account_id;
  }

  // ATENÇÃO: a assinatura POSICIONAL é intocável. Há um call site que passa um
  // objeto inteiro como `funnelId` (settings/reports/KanbanReports.vue:1457, bug
  // pré-existente); qualquer troca de posição o pioraria em silêncio. Params novos
  // entram sempre por `filters` (5º argumento), como shape/from/to/date_basis.
  async getItems(
    funnelId,
    page = 1,
    stageId = null,
    agentId = null,
    filters = {}
  ) {
    const selectedFunnel = store.getters['funnel/getSelectedFunnel'];
    const finalFunnelId = funnelId || selectedFunnel?.id;
    const dateStart = filters.dateStart || filters.date_start;
    const dateEnd = filters.dateEnd || filters.date_end || dateStart;
    const valueMin = filters.valueMin ?? filters.value_min;
    const valueMax = filters.valueMax ?? filters.value_max;
    const perPage = filters.perPage ?? filters.per_page;

    if (!finalFunnelId) {
      throw new Error('Nenhum funil selecionado ou fornecido');
    }

    const params = {
      funnel_id: finalFunnelId,
      stage_id: stageId, // Sempre incluir stage_id para evitar itens ocultos
      page: page > 1 ? page : undefined,
      agent_id: agentId || undefined,
      date_start: dateStart,
      date_end: dateEnd,
      priorities: filters.priorities || filters.priority || undefined,
      status: filters.status || undefined,
      channel: filters.channel || undefined,
      value_min: valueMin,
      value_max: valueMax,
      per_page: perPage,
      show_won: filters.show_won,
      show_lost: filters.show_lost,
    };
    if (filters.shape) params.shape = filters.shape;
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    if (filters.date_basis) params.date_basis = filters.date_basis;
    // Carregamento por coluna (board 2.0). O backend só entra no ramo novo quando
    // shape=v2 E per_stage estão presentes; nenhum outro consumidor manda per_stage.
    if (filters.per_stage) params.per_stage = filters.per_stage;
    if (filters.stages) params.stages = filters.stages;
    if (filters.cursor) params.cursor = filters.cursor;
    // Busca e filtro de agente do board 2.0 (server-side): o backend filtra por
    // titulo/contato (query) e por assignee OR assigned_agents (agent_id).
    if (filters.query) params.query = filters.query;
    if (filters.agent_id) params.agent_id = filters.agent_id;
    // Kanban 2.0 / Fase 6 — SLA de follow-up: estado(s) do filtro (lista
    // separada por vírgula, ESCALAR: o backend recebe `followup_sla` por Strong
    // Parameters escalar e um `followup_sla[]` seria descartado em silêncio) e a
    // ordenação alternativa (`sort=followup_sla`).
    //
    // ATENÇÃO: este método monta a lista de params EXPLICITAMENTE e descarta
    // qualquer chave de `filters` que não esteja aqui. Param novo que não ganhe a
    // sua linha nunca sai do navegador, e o board volta sem o filtro — sem erro
    // nenhum na tela.
    if (filters.followup_sla) params.followup_sla = filters.followup_sla;
    if (filters.sort) params.sort = filters.sort;
    // 🐛 `team_id` caiu exatamente na armadilha do aviso acima (09/09). O
    // `buildBoardParams` do useKanban monta o parâmetro desde o filtro de equipe do
    // cabeçalho, e o backend o espera — `assigned_to_team` no build_base_query, com
    // `:team_id` nos Strong Parameters. Faltava só esta linha no meio: o seletor
    // "Selecionar equipe" mudava de estado na tela e o board voltava idêntico, sem
    // erro em lugar nenhum.
    if (filters.team_id) params.team_id = filters.team_id;

    // Remover parâmetros undefined
    Object.keys(params).forEach(
      key => params[key] === undefined && delete params[key]
    );

    // Usar axios diretamente para garantir que os parâmetros sejam enviados
    const options = { params, timeout: 20_000 };
    // Só o board 2.0 manda params aninhados; o caminho v1 mantém a serialização
    // padrão do axios, byte a byte.
    if (params.stages || params.cursor) {
      options.paramsSerializer = serializeKanbanParams;
    }
    return axios.get(this.url, options);
  }

  async getItem(itemId, params = {}) {
    const response = await axios.get(`${this.url}/${itemId}`, { params });
    return response;
  }

  async submitQualification(itemId, answers) {
    const response = await axios.patch(`${this.url}/${itemId}/qualification`, {
      answers,
    });
    return response.data;
  }

  async createItem(data) {
    const response = await axios.post(this.url, {
      kanban_item: data,
    });
    if (!response.data?.existing) {
      await webhookService.notifyItemCreated(response.data).catch(() => null);
    }
    return response;
  }

  // Ponto de entrada CANÔNICO para iniciar uma negociação (Kanban 2.0). Delega ao
  // Kanban::StartNegotiationService — o frontend NUNCA cria KanbanItem direto
  // (ADR-018/020). O endpoint legado /kanban_items retorna 422 em contas Kanban 2.0.
  // conversationId = display_id da conversa (a rota resolve por display_id). O backend
  // resolve o funil default quando funnel_id não é enviado — o front só solicita.
  async startNegotiation(conversationId, payload = {}) {
    const response = await axios.post(
      `${this.baseUrl()}/conversations/${conversationId}/negotiation`,
      payload
    );
    return response;
  }

  // Registra o motivo de perda/desqualificação num item que já está numa etapa de perda
  // (modelo de etapa única). Delega ao RegisterLossReasonService — NÃO move de etapa.
  // PROTEÇÃO ATIVA (ADR-023). Suspende as AUTOMAÇÕES sobre a oportunidade — SLA de
  // follow-up, perda automática, remalho e redistribuição automática — sem jamais
  // bloquear a decisão manual de um usuário autorizado.
  //
  // `reason` é OBRIGATÓRIO no backend (Kanban::ActivateProtectionService): proteção sem
  // motivo é uma trava que ninguém consegue auditar depois. `protectionUntil` é opcional
  // (ISO 8601); sem ele a proteção não tem prazo e só sai por remoção manual.
  //
  // Papel: a policy é `agent_allowed: false, supervisor_allowed: true` — consultor NÃO
  // ativa nem remove; supervisor/administrator/diretor sim.
  async activateProtection(itemId, { reason, protectionUntil } = {}) {
    const payload = { reason };
    if (protectionUntil) payload.protection_until = protectionUntil;
    const response = await axios.post(
      `${this.url}/${itemId}/activate_protection`,
      payload
    );
    return response;
  }

  async removeProtection(itemId) {
    const response = await axios.post(
      `${this.url}/${itemId}/remove_protection`
    );
    return response;
  }

  async registerLossReason(itemId, lossReasonId) {
    const response = await axios.post(`${this.url}/${itemId}/loss_reason`, {
      loss_reason_id: lossReasonId,
    });
    return response;
  }

  // Cria uma nova oportunidade (KanbanItem) a partir do FAB "Nova
  // oportunidade" do board. O backend faz find-or-create do contato com
  // dedup — o frontend NAO cria contato. payload aceita um contato existente
  // (contact_id) OU os campos de um contato novo (contact: { name,
  // phone_number, email }), sempre com funnel_id.
  async createOpportunity(payload) {
    const response = await axios.post(
      `${this.baseUrl()}/kanban/opportunities`,
      payload
    );
    return response;
  }

  // Formulário de criação JÁ RESOLVIDO pelo backend: a equipe (sempre a do funil) e
  // quais consultores o usuário pode escolher. O front só desenha (ADR-020). Envia o
  // funnel_id para o backend herdar a equipe atribuída ao funil.
  // consultant_id (opcional) é quem o backend usa para resolver `funnels`/
  // `funnel_ambiguous`: os funis em que ESSE vendedor está em settings['agents']. Sem
  // ele (modo 'select', antes de escolher o vendedor), o backend não tem de quem
  // calcular e devolve tudo ambíguo.
  async getOpportunityCreationOptions(funnelId, consultantId) {
    return axios.get(`${this.baseUrl()}/kanban/opportunity_creation/options`, {
      params: { funnel_id: funnelId, consultant_id: consultantId },
    });
  }

  // Veredito de atendimento para o par (contato, vendedor, funil): decide se a
  // oportunidade nasce em conversa NOVA (mode: 'new'), é vinculada a uma conversa já
  // aberta (mode: 'link', conversation_display_id) ou fica BLOQUEADA (reason_code). O
  // front só pergunta — a regra de domínio inteira mora no backend (ADR-020). Em
  // bloqueio o servidor manda só o CÓDIGO, nunca prosa: quem escolhe o texto é a tela
  // (esse vazamento — nome de consultor/franquia — já voltou três vezes neste projeto).
  async attendanceVerdict({ contactId, consultantId, funnelId }) {
    return axios.get(
      `${this.baseUrl()}/kanban/opportunity_creation/attendance_verdict`,
      {
        params: {
          contact_id: contactId,
          consultant_id: consultantId,
          funnel_id: funnelId,
        },
      }
    );
  }

  // Fluxo de aprovacao: o supervisor aprova o pedido do consultor e o backend
  // cria o KanbanItem no board. Responde 200 (item) / 422 { error }.
  async approveOpportunityRequest(requestId) {
    return axios.post(
      `${this.baseUrl()}/kanban/opportunity_requests/${requestId}/approve`
    );
  }

  // Fluxo de aprovacao: o supervisor recusa o pedido com um motivo obrigatorio.
  // Responde 200 / 422 { error }.
  async rejectOpportunityRequest(requestId, reason) {
    return axios.post(
      `${this.baseUrl()}/kanban/opportunity_requests/${requestId}/reject`,
      { reason }
    );
  }

  async updateItem(itemId, data) {
    try {
      const { data: currentItem } = await this.getItem(itemId);
      const response = await axios.put(`${this.url}/${itemId}`, {
        kanban_item: data,
      });

      const changes = {};
      Object.keys(data).forEach(key => {
        if (JSON.stringify(currentItem[key]) !== JSON.stringify(data[key])) {
          changes[key] = {
            from: currentItem[key],
            to: data[key],
          };
        }
      });

      if (Object.keys(changes).length > 0) {
        await webhookService.notifyItemUpdated(response.data, changes);
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  async deleteItem(itemId) {
    try {
      const { data: currentItem } = await this.getItem(itemId);
      const response = await axios.delete(`${this.url}/${itemId}`);

      await webhookService.notifyItemDeleted(currentItem);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Aceita 2 formas:
  //   moveToStage(itemId, 'perdido_definido', 30)
  //   moveToStage(itemId, { funnel_stage, funnel_id, item_details, won_sale })
  async moveToStage(itemId, funnelStageOrPayload, funnelId = null) {
    try {
      // Normaliza para sempre trabalhar com um payload-objeto.
      const isPayloadObject =
        typeof funnelStageOrPayload === 'object' &&
        funnelStageOrPayload !== null;
      const funnelStage = isPayloadObject
        ? funnelStageOrPayload.funnel_stage
        : funnelStageOrPayload;
      const resolvedFunnelId = isPayloadObject
        ? funnelStageOrPayload.funnel_id
        : funnelId;
      const itemDetails = isPayloadObject
        ? funnelStageOrPayload.item_details
        : null;
      const wonSale = isPayloadObject
        ? funnelStageOrPayload.won_sale || itemDetails?.won_sale
        : null;

      const payload = {
        funnel_stage: funnelStage,
        stage_entered_at: new Date().toISOString(),
      };
      if (resolvedFunnelId) payload.funnel_id = resolvedFunnelId;
      if (itemDetails) payload.item_details = itemDetails;
      if (wonSale) payload.won_sale = wonSale;

      const { data: updatedItem } = await axios.post(
        `${this.url}/${itemId}/move_to_stage`,
        payload
      );

      return updatedItem || null;
    } catch (error) {
      throw error;
    }
  }

  // Corrige o valor de uma venda JA GANHA. Endpoint dedicado (nao reaproveita
  // moveToStage/update): mandar so `value` ali reconstruiria o WonSale inteiro a
  // partir de um payload incompleto - ver WonSales::UpdateValueService no backend.
  // `dataCompra` é opcional: sem ela o backend mantém a data que já estava
  // (mandar em branco é erro lá, não "apagar" — a coluna é obrigatória).
  async updateProductionValue(itemId, value, dataCompra) {
    const payload = { value };
    if (dataCompra !== undefined) payload.data_compra = dataCompra;
    const { data: updatedItem } = await axios.patch(
      `${this.url}/${itemId}/production_value`,
      payload
    );
    return updatedItem;
  }

  async moveToFunnel(itemId, funnelId, funnelStage = null) {
    try {
      const { data: currentItem } = await this.getItem(itemId);
      const oldFunnelId = currentItem.funnel_id;
      const oldStage = currentItem.funnel_stage;

      const payload = {
        funnel_id: funnelId,
      };
      if (funnelStage) {
        payload.funnel_stage = funnelStage;
        payload.stage_entered_at = new Date().toISOString();
      }

      const response = await axios.post(`${this.url}/${itemId}/move`, payload);

      const { data: updatedItem } = await this.getItem(itemId);

      if (updatedItem) {
        // Notificar mudança de funil ou estágio
        const changes = {};
        if (oldFunnelId !== funnelId) {
          changes.funnel_id = { from: oldFunnelId, to: funnelId };
        }
        if (funnelStage && oldStage !== funnelStage) {
          changes.funnel_stage = { from: oldStage, to: funnelStage };
          await webhookService.notifyStageChange(
            updatedItem,
            oldStage,
            funnelStage
          );
        }

        if (Object.keys(changes).length > 0) {
          await webhookService.notifyItemUpdated(updatedItem, changes);
        }

        return updatedItem;
      } else {
        return null;
      }
    } catch (error) {
      throw error;
    }
  }

  async reorderItems(positions) {
    try {
      const currentItems = await Promise.all(
        positions.map(pos => this.getItem(pos.id))
      );

      const response = await axios.post(`${this.url}/reorder`, {
        positions,
      });

      const changes = positions.map(pos => ({
        item_id: pos.id,
        old_position: currentItems.find(item => item.data.id === pos.id)?.data
          .position,
        new_position: pos.position,
      }));

      await webhookService.notifyItemReordered(response.data, changes);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async startTimer(itemId) {
    try {
      const response = await this.updateItem(itemId, {
        timer_started_at: new Date().toISOString(),
      });

      await webhookService.notifyTimerStarted(response.data);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async stopTimer(itemId) {
    try {
      const response = await this.updateItem(itemId, {
        timer_started_at: null,
      });

      const duration = response.data.timer_duration;
      await webhookService.notifyTimerStopped(response.data, duration);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getItemByConversationId(conversationId) {
    const response = await this.get('', {
      params: {
        'item_details.conversation_id': conversationId,
      },
    });
    return response;
  }

  // Métodos para gerenciar agentes atribuídos
  async assignAgent(itemId, agentId) {
    try {
      const response = await axios.post(`${this.url}/${itemId}/assign_agent`, {
        agent_id: agentId,
      });

      // Atualizar o store com os novos dados
      store.dispatch('kanban/updateItemDetails', {
        itemId,
        details: response.data,
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  async removeAgent(itemId, agentId) {
    try {
      const response = await axios.delete(
        `${this.url}/${itemId}/remove_agent`,
        {
          data: {
            agent_id: agentId,
          },
        }
      );

      // Atualizar o store com os novos dados
      store.dispatch('kanban/updateItemDetails', {
        itemId,
        details: response.data,
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  async getAssignedAgents(itemId) {
    try {
      const response = await axios.get(`${this.url}/${itemId}/assigned_agents`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getWhatsAppAvailability(itemId) {
    return axios.get(`${this.url}/${itemId}/whatsapp_availability`);
  }

  // Automations
  async getAutomations() {
    try {
      const response = await axios.get(
        `/api/v1/accounts/${this.accountId}/kanban/automations`
      );
      return response;
    } catch (error) {
      return { data: [] };
    }
  }

  async getAutomation(id) {
    const response = await axios.get(
      `/api/v1/accounts/${this.accountId}/kanban/automations/${id}`
    );
    return response;
  }

  async createAutomation(data) {
    const response = await axios.post(
      `/api/v1/accounts/${this.accountId}/kanban/automations`,
      { kanban_automation: data }
    );
    return response;
  }

  async updateAutomation(id, data) {
    const response = await axios.patch(
      `/api/v1/accounts/${this.accountId}/kanban/automations/${id}`,
      { kanban_automation: data }
    );
    return response;
  }

  async deleteAutomation(id) {
    const response = await axios.delete(
      `/api/v1/accounts/${this.accountId}/kanban/automations/${id}`
    );
    return response;
  }

  async getBatchData(funnelId) {
    const response = await axios.get(`${this.url}/batch`, {
      params: {
        funnel_id: funnelId,
      },
    });

    return response.data;
  }

  async searchItems(query, funnelId = null, agentId = null) {
    try {
      const selectedFunnel = store.getters['funnel/getSelectedFunnel'];
      const finalFunnelId = funnelId || selectedFunnel?.id;

      if (!finalFunnelId) {
        throw new Error('Nenhum funil selecionado ou fornecido');
      }

      const params = {
        query: query,
        funnel_id: finalFunnelId,
        agent_id: agentId || undefined,
      };

      // Remover parâmetros undefined
      Object.keys(params).forEach(
        key => params[key] === undefined && delete params[key]
      );

      const response = await axios.get(`${this.url}/search`, { params });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async filterItems(filters = {}, funnelId = null) {
    try {
      const selectedFunnel = store.getters['funnel/getSelectedFunnel'];
      const finalFunnelId = funnelId || selectedFunnel?.id;

      if (!finalFunnelId) {
        throw new Error('Nenhum funil selecionado ou fornecido');
      }

      const params = {
        funnel_id: finalFunnelId,
        priorities: filters.priorities || undefined,
        value_min: filters.valueMin || undefined,
        value_max: filters.valueMax || undefined,
        agent_id: filters.agent || undefined,
        date_start: filters.dateStart || undefined,
        date_end: filters.dateEnd || undefined,
        scheduled_date_start: filters.scheduledDateStart || undefined,
        scheduled_date_end: filters.scheduledDateEnd || undefined,
      };

      // Remover parâmetros undefined ou arrays vazios
      Object.keys(params).forEach(key => {
        if (
          params[key] === undefined ||
          (Array.isArray(params[key]) && params[key].length === 0)
        ) {
          delete params[key];
        }
      });

      const response = await axios.get(`${this.url}/filter`, { params });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getReports(params = {}) {
    try {
      const response = await axios.get(`${this.url}/reports`, {
        params: {
          funnel_id: params.funnel_id,
          from: params.from,
          to: params.to,
          user_ids: params.user_ids,
          inbox_id: params.inbox_id,
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para atribuir agente a um item específico do checklist
  async assignAgentToChecklistItem(itemId, checklistItemId, agentId) {
    try {
      const response = await axios.post(
        `${this.url}/${itemId}/assign_agent_to_checklist_item`,
        {
          checklist_item_id: checklistItemId,
          agent_id: agentId,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para remover agente de um item específico do checklist
  async removeAgentFromChecklistItem(itemId, checklistItemId) {
    try {
      const response = await axios.delete(
        `${this.url}/${itemId}/remove_agent_from_checklist_item`,
        {
          data: {
            checklist_item_id: checklistItemId,
          },
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para criar checklist item
  async createChecklistItem(itemId, checklistData) {
    try {
      const response = await axios.post(
        `${this.url}/${itemId}/create_checklist_item`,
        {
          text: checklistData.text,
          attachments: checklistData.attachments || [],
          linked_item_id: checklistData.linked_item_id,
          linked_conversation_id: checklistData.linked_conversation_id,
          linked_contact_id: checklistData.linked_contact_id,
          due_date: checklistData.due_date,
          priority: checklistData.priority || 'none',
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para atualizar checklist item
  async updateChecklistItem(itemId, checklistItemId, updateData) {
    try {
      const response = await axios.patch(
        `${this.url}/${itemId}/update_checklist_item`,
        {
          checklist_item_id: checklistItemId,
          ...updateData,
        }
      );

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para marcar/desmarcar item do checklist
  async toggleChecklistItem(itemId, checklistItemId) {
    try {
      const response = await axios.post(
        `${this.url}/${itemId}/toggle_checklist_item`,
        {
          checklist_item_id: checklistItemId,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para deletar checklist item
  async deleteChecklistItem(itemId, checklistItemId) {
    try {
      const response = await axios.delete(
        `${this.url}/${itemId}/delete_checklist_item`,
        {
          data: {
            checklist_item_id: checklistItemId,
          },
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para criar nota
  async createNote(itemId, noteData) {
    try {
      const response = await axios.post(`${this.url}/${itemId}/create_note`, {
        text: noteData.text,
        attachments: noteData.attachments || [],
        linked_item_id: noteData.linked_item_id,
        linked_conversation_id: noteData.linked_conversation_id,
        linked_contact_id: noteData.linked_contact_id,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para atualizar nota
  async updateNote(itemId, noteId, noteData) {
    try {
      const response = await axios.patch(`${this.url}/${itemId}/update_note`, {
        note_id: noteId,
        text: noteData.text,
        attachments: noteData.attachments || [],
        linked_item_id: noteData.linked_item_id,
        linked_conversation_id: noteData.linked_conversation_id,
        linked_contact_id: noteData.linked_contact_id,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Mensagens da conversa vinculada ao KanbanItem (Drawer, aba Conversa). Endpoint
  // próprio do fork: a rota genérica do Chatwoot (GET conversations/:id/messages) é
  // protegida por uma policy que exige o usuário ser membro da inbox da conversa;
  // no Kanban a atribuição do consultor não cria esse vínculo, e a chamada dava 401
  // para ~10,5% dos leads. Aqui a autorização é a mesma que decide os cards do board.
  // Resposta idêntica à do Chatwoot ({ meta, payload }): muda só a URL e o
  // identificador, que passa a ser o id do KanbanItem.
  //
  // `before`: página anterior (rolar para cima). `after`: só o que chegou depois dessa
  // mensagem — é como o drawer acompanha a conversa ao vivo. O MessageFinder do Chatwoot
  // aceita os dois.
  async getMessages(itemId, { before, after } = {}) {
    const params = {};
    if (before !== undefined && before !== null) params.before = before;
    if (after !== undefined && after !== null) params.after = after;
    return axios.get(`${this.url}/${itemId}/messages`, { params });
  }

  // Método para buscar notas do item
  async getNotes(itemId) {
    try {
      const response = await axios.get(`${this.url}/${itemId}/get_notes`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para buscar checklist do item
  async getChecklists(itemId) {
    try {
      const response = await axios.get(`${this.url}/${itemId}/get_checklist`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para deletar nota
  async deleteNote(itemId, noteId) {
    try {
      const response = await axios.delete(`${this.url}/${itemId}/delete_note`, {
        data: {
          note_id: noteId,
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para fazer upload de anexo do item kanban
  async uploadAttachment(itemId, file) {
    try {
      const formData = new FormData();
      formData.append('attachment', file);

      // Usar a URL correta para attachments: kanban/items ao invés de kanban_items
      const attachmentUrl = `${this.baseUrl()}/kanban/items/${itemId}/attachments`;

      const response = await axios.post(attachmentUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para deletar anexo do item kanban
  async deleteAttachment(itemId, attachmentId) {
    try {
      // Usar a URL correta para attachments: kanban/items ao invés de kanban_items
      const attachmentUrl = `${this.baseUrl()}/kanban/items/${itemId}/attachments/${attachmentId}`;

      const response = await axios.delete(attachmentUrl);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para obter todos os anexos de um item kanban
  async getAttachments(itemId) {
    try {
      // Usar a URL correta para attachments: kanban/items ao invés de kanban_items
      const attachmentUrl = `${this.baseUrl()}/kanban/items/${itemId}/attachments`;

      const response = await axios.get(attachmentUrl);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para obter relatório de tempo do item
  async getTimeReport(itemId) {
    try {
      const response = await axios.get(`${this.url}/${itemId}/time_report`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para obter detalhamento de tempo por etapa
  async getStageTimeBreakdown(itemId) {
    try {
      const response = await axios.get(
        `${this.url}/${itemId}/stage_time_breakdown`
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para duplicar checklist para outro item
  async duplicateChecklist(sourceItemId, targetItemId, options = {}) {
    try {
      const response = await axios.post(
        `${this.url}/${sourceItemId}/duplicate_checklist`,
        {
          target_item_id: targetItemId,
          merge: options.merge || false,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para buscar itens no checklist
  async searchChecklist(itemId, query = '') {
    try {
      const params = query ? { query } : {};
      const response = await axios.get(
        `${this.url}/${itemId}/search_checklist`,
        { params }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para obter progresso do checklist por agente
  async getChecklistProgressByAgent(itemId) {
    try {
      const response = await axios.get(
        `${this.url}/${itemId}/checklist_progress_by_agent`
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para obter counts de notas, checklist e anexos
  async getCounts(itemId) {
    try {
      const response = await axios.get(`${this.url}/${itemId}/counts`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // AÇÕES EM MASSA

  // Método para mover múltiplos itens para uma nova etapa
  async bulkMoveItems(itemIds, newStage, funnelId = null) {
    try {
      const payload = {
        item_ids: itemIds,
        new_stage: newStage,
      };

      if (funnelId) {
        payload.funnel_id = funnelId;
      }

      const response = await axios.post(`${this.url}/bulk_move_items`, payload);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para atribuir agente para múltiplos itens
  async bulkAssignAgent(itemIds, agentId, mode = 'replace') {
    try {
      const response = await axios.post(`${this.url}/bulk_assign_agent`, {
        item_ids: itemIds,
        agent_id: agentId,
        mode: mode, // 'replace' ou 'add'
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para definir prioridade para múltiplos itens
  async bulkSetPriority(itemIds, priority) {
    try {
      const response = await axios.post(`${this.url}/bulk_set_priority`, {
        item_ids: itemIds,
        priority: priority,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para mudar o status do item
  async changeStatus(itemId, status) {
    try {
      const response = await axios.post(`${this.url}/${itemId}/change_status`, {
        status: status,
      });

      await webhookService.notifyItemUpdated(response.data, {
        status: { from: null, to: status },
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para exportar itens do funil em CSV/XLSX
  async exportItems(funnelId, filters = {}) {
    try {
      const format = filters.format || 'xlsx';
      const params = {
        funnel_id: funnelId,
        file_format: format,
        priorities: filters.priorities || undefined,
        value_min: filters.valueMin || undefined,
        value_max: filters.valueMax || undefined,
        agent_id: filters.agent || undefined,
        date_start: filters.dateStart || undefined,
        date_end: filters.dateEnd || undefined,
        scheduled_date_start: filters.scheduledDateStart || undefined,
        scheduled_date_end: filters.scheduledDateEnd || undefined,
      };

      // Remover parâmetros undefined ou arrays vazios
      Object.keys(params).forEach(key => {
        if (
          params[key] === undefined ||
          (Array.isArray(params[key]) && params[key].length === 0)
        ) {
          delete params[key];
        }
      });

      const response = await axios.get(`${this.url}/export`, {
        params,
        responseType: 'blob',
      });

      // Criar link para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Extrair nome do arquivo do header Content-Disposition ou usar padrão
      const contentDisposition = response.headers['content-disposition'];
      let filename = `kanban_items_${funnelId}_${new Date().getTime()}.${format}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para fazer preview do CSV antes da importação
  async importPreview(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${this.url}/import_preview`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para importar itens do CSV
  async importItems(funnelId, file, mappings, defaultStageId = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('funnel_id', funnelId);
      formData.append('mappings', JSON.stringify(mappings));
      if (defaultStageId) {
        formData.append('default_stage_id', defaultStageId);
      }

      const response = await axios.post(`${this.url}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response;
    } catch (error) {
      throw error;
    }
  }
}

export default new KanbanAPI();
